// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartFormUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Report Part Form Content Utils
 */

import ReportPartElement from './Izenda.ReportPartElement';
import ReportPartFormConstants from 'logic/data/Izenda.ReportPartFormConstants';
import {
  FieldPropertiesPaths,
  ExcludeOnHtmlViewPaths,
  InitialFieldPropertiesPaths
} from 'logic/data/Izenda.FormFieldPropertiesPaths';
import embedFetcher from 'fetchers/Izenda.EmbedReportDataFetcher';
import TinyMCEConstants from 'logic/data/Izenda.TinyMCEConstants';
import reportBodyConstants from 'logic/data/Izenda.ReportBodyConstants';
import {
  forEach,
  set,
  flow,
  values,
  omit,
  filter,
  get,
  orderBy,
  find,
  findIndex,
  split,
  flatMap,
  isNil,
  template,
  trim,
  cloneDeep,
  isEmpty,
  head,
  difference,
  isArray
} from 'lodash/fp';

/**
 * Get list node by its node name.
 * @param  {String} htmlString
 * @param  {String} nodeName
 * @return {String} name
 */
const filterByNodeName = (htmlString: string, nodeName: string): string => {
  return $izenda('<div>' + htmlString + '</div>').find(nodeName);
};

/**
 * Transform a field-prop tag to a ReportPartElement object
 * @param  {Array} nodes
 * @param  {Object} baseObject
 * @return {Object} ReportPartElement
 */
const mapFromHTMLToObject = (nodes: any, baseObject: any): any => {
  let obj = {};
  const listKey = [];
  const element = new ReportPartElement();

  forEach(node => {
    const $node = $izenda(node);
    const key = $node.attr('key');
    const keySetting = FieldPropertiesPaths[key];
    if (keySetting) {
      const value = keySetting.dataType.parseValue($node.attr('value'));

      obj = set(keySetting.path, value, obj);
      listKey.push(key);
    }
  }, nodes);

  // because the setting for default value for data type is different with default
  // value set in report part element property
  // Ex: sort is string and default value is empty, so when sort is empty we don't generate HTML for it
  // when we read back from HTML because in HTML don't have node for sort so when we create new element object in
  // (:95) it will get the default value in report part element property which is ASC. thus change the value of field
  flow(
    omit(listKey),
    values,
    filter({ needDefaultValue: true }),
    forEach(keySetting => {
      const value = get(keySetting.path, element);

      obj = set(keySetting.path, value, obj);
    })
  )(FieldPropertiesPaths);

  // Reset functionInfo to null if there is not selected function.
  const funcInfoIdKey = 'funcInfoId';
  if (!get(FieldPropertiesPaths[funcInfoIdKey].path, obj)) {
    obj = set(FieldPropertiesPaths[funcInfoIdKey].path, null, obj);
  }

  // if baseObject is null or undifined. That means the field was disabled in Data Model.
  // So we could not get the default value from data tree
  // because that field is not available for report
  // then we need to set the element is invisible
  if (isNil(baseObject) || isNil(baseObject.field)) {
    element.field.visible = false;
  } else {
    element.readData(baseObject);
  }

  element.readData(obj);

  // Set default fieldItemVisible based on visible of field.
  if (isNil(get('properties.fieldItemVisible', element)) && element.field) {
    element.properties.fieldItemVisible = element.field.visible;
  }

  return element;
};

/**
 * Transform html content to ReportPartElement objects
 * @param  {Array} originalColumns
 * @param  {Array} originalSeparators
 * @param  {String} htmlString
 * @param  {Object} dataTree
 * @return void
 */
const syncFromHTMLToElements = (
  originalColumns: any,
  originalSeparators: any,
  htmlString: string,
  dataTree: any
): void => {
  const columns = [];
  const separators = [];
  const fields = filterByNodeName(htmlString, 'field');

  // build element from html
  fields.each((index, field) => {
    let fieldName = $izenda(field).attr('field-name');
    let fieldNameAlias = trim($izenda(field).text());

    if (fieldNameAlias === reportBodyConstants.FIELD_HIDDEN_LABEL) {
      fieldName = $izenda(field).attr('field-name');
      const matches = fieldName.match(/\[(.*?)\]/g);
      const lastMatched = matches[matches.length - 1].replace('[', '').replace(']', '');
      fieldNameAlias = lastMatched;
    }

    const fieldProps = filterByNodeName(field.innerHTML, 'field-prop');
    let element = {};

    // load default value which is get from field-name.
    const defaultField = dataTree && fieldName && parseFieldNameIntoFieldProps(fieldName, dataTree);
    element.field = cloneDeep(defaultField);
    // update with current html value.
    element = mapFromHTMLToObject(fieldProps, element);

    // Update field name alias
    // - The value of name, fieldNameAlias, and text of each element must be same.
    // - Cannot use  set here, because we want to use computed value later.
    element.field && (element.field.fieldNameAlias = fieldNameAlias);
    element.name = fieldNameAlias;
    element.position = index;

    columns.push(element);
  });

  const impactElements = updateReportPartContent(columns, originalColumns.elements, originalSeparators.elements);
  updateReportPartContent(separators, originalSeparators.elements, impactElements);

  updateCorrectPosition(originalColumns);
  updateCorrectPosition(originalSeparators);
};

/**
 * Update position of elements inside container after move between container
 * @param {Object} container
 * @return void
 */
const updateCorrectPosition = (container: any): void => {
  container.elements = orderBy(['position'], ['asc'], container.elements);
  const elementsLength = container.elements.length;
  for (let i = 0; i < elementsLength; i++) {
    container.elements[i].position = i;
  }
};

/**
 * Update report part content
 * @param {Array} elements
 * @param {Array} originalElements
 * @return {Object} ReportPartElement
 */
const updateReportPartContent = (elements: any, originalElements: any, impactElements: any): any => {
  const removedElements = [];
  // remove
  for (let i = originalElements.length - 1; i >= 0; i--) {
    const matchingItem = find(item => item.name == originalElements[i].name, elements);

    if (!matchingItem) {
      removedElements.push(originalElements[i]);
      originalElements.remove(originalElements[i]);
    }
  }

  // update
  for (let i = 0; i < originalElements.length; i++) {
    const element = originalElements[i];
    const index = findIndex(item => item.name == element.name, elements);

    if (index !== -1) {
      originalElements[i].readData(elements[index]);
      elements.splice(index, 1);
    }
  }

  // insert
  forEach(element => {
    const newElement = find({ fieldNameAlias: element.fieldNameAlias }, impactElements);
    originalElements.push(isNil(newElement) ? element : newElement);
  }, elements);

  return removedElements;
};

/**
 * Build field object which is used in form definition.
 * @param  {Object} querysource
 * @param  {Object} field
 * @return {Object} field
 */
const buildField = (querysource: any, field: any): any => {
  return (
    querysource &&
    field && {
      fieldId: field.id,
      fieldName: field.name,
      fieldNameAlias: field.alias || field.name,
      dataFieldType: field.izendaDataType,
      querySourceId: field.querySourceId,
      querySourceType: querysource.isAlias ? null : querysource.type,
      sourceAlias: querysource.name,
      relationshipId: querysource.isAlias ? querysource.id : null,
      visible: field.visible,
      calculatedTree: field.calculatedTree,
      schemaName: querysource.querySourceCategoryName,
      querySourceName: querysource.name,
      databaseName: querysource.connectionName,
      isCalculated: field.isCalculated
    }
  );
};

/**
 * Find fields in query sources
 * @param  {Object} querySources
 * @param  {Function} filterFn
 * @return {Array} match items
 */
const findFields = (querySources: any, filterFn: () => mixed): any => {
  const matchingItems = [];

  forEach(querysource => {
    const matchingField = querysource.fields && find(item => filterFn(querysource, item), querysource.fields);

    if (matchingField) {
      matchingItems.push(buildField(querysource, matchingField));
    }
  }, querySources);

  return !isEmpty(matchingItems) && matchingItems;
};

/**
 * Build a field object in form definition from field-name attribute.
 * @param  {String} fieldName
 * @param  {Object} dataTree
 * @return {Object} field
 */
const parseFieldNameIntoFieldProps = (fieldName: any, dataTree: any): any =>
  head(buildFieldsByFieldNameHtml(fieldName, dataTree));

/**
 * Build field object(s) in form definition from field-name attribute.
 * @param  {String} fieldName
 * @param  {Object} dataTree
 * @return {Object} field
 */
const buildFieldsByFieldNameHtml = (fieldName: string, dataTree: any): any => {
  fieldName = fieldName.substring(1, fieldName.length - 1);
  const sections = split('].[', fieldName);
  const numOfSections = sections.length;
  let fields = null;

  const querySources = flatMap(item => item.querySource.toJS(), dataTree.details);

  /**
   * Validate data source name
   * @param  {Object} querysource
   * @param  {String} name
   */
  const checkDataSourceName = (querysource, name) =>
    (querysource.isAlias && querysource.name === name) ||
    (!querysource.isAlias && (querysource.originalName === name || querysource.name === name));

  switch (numOfSections) {
    case 1: {
      // [field name alias]
      // [field actual name]
      const firstOperand = sections[0];
      fields =
        findFields(querySources, (querysource, field) => {
          return field.alias === firstOperand;
        }) ||
        findFields(querySources, (querysource, field) => {
          return field.name === firstOperand;
        });
      break;
    }
    case 2: {
      // [data source name].[field name alias] (|| [data source alias].[field name alias])
      // [data source name].[field actual name]
      // [join alias].[field name alias]
      // [join alias].[field actual name]
      const firstOperand = sections[0];
      const secondOperand = sections[1];
      fields =
        findFields(querySources, (querysource, field) => {
          return checkDataSourceName(querysource, firstOperand) && field.alias === secondOperand;
        }) ||
        findFields(querySources, (querysource, field) => {
          return checkDataSourceName(querysource, firstOperand) && field.name === secondOperand;
        });
      break;
    }
    case 3: {
      // [schema name].[data source name].[field name alias]
      // [schema name].[data source name].[field actual name]
      const firstOperand = sections[0];
      const secondOperand = sections[1];
      const thirdOperand = sections[2];
      fields =
        findFields(querySources, (querysource, field) => {
          return (
            querysource.querySourceCategoryName === firstOperand &&
            checkDataSourceName(querysource, secondOperand) &&
            field.alias === thirdOperand
          );
        }) ||
        findFields(querySources, (querysource, field) => {
          return (
            querysource.querySourceCategoryName === firstOperand &&
            checkDataSourceName(querysource, secondOperand) &&
            field.name === thirdOperand
          );
        });
      break;
    }
    case 4: {
      // [database name].[schema name].[data source name].[field name alias]
      // [database name].[schema name].[data source name].[field actual name]
      const firstOperand = sections[0];
      const secondOperand = sections[1];
      const thirdOperand = sections[2];
      const fourthOperand = sections[3];
      fields =
        findFields(querySources, (querysource, field) => {
          return (
            querysource.connectionName === firstOperand &&
            querysource.querySourceCategoryName === secondOperand &&
            checkDataSourceName(querysource, thirdOperand) &&
            field.alias === fourthOperand
          );
        }) ||
        findFields(querySources, (querysource, field) => {
          return (
            querysource.connectionName === firstOperand &&
            querysource.querySourceCategoryName === secondOperand &&
            checkDataSourceName(querysource, thirdOperand) &&
            field.name === fourthOperand
          );
        });
      break;
    }
  }

  // If there is not any matched field then fields' value is false.
  // We return null for this case.
  return fields ? fields : null;
};

/**
 * Validate a field tag with current context.
 * @param  {Object} field
 * @param  {Object} context
 * @return {Object} result
 */
const validateField = (field: any, context: any): any => {
  const validations = [];
  validations.push(validateFieldName);
  validations.push(validateFieldNameAlias);

  let result = null;
  let index = 0;
  while (isNil(result) && index < validations.length) {
    result = validations[index](field, context);
    index++;
  }

  return result;
};

/**
 * Validate field name.
 * @param  {Object} field
 * @param  {Object} context
 * @return {String} validate messsage
 */
const validateFieldName = (field: any, context: any): string => {
  const dataTree = context.dataTree;
  if (isNil(dataTree)) return null;

  const fieldName = $izenda(field).attr('field-name');
  const fields = buildFieldsByFieldNameHtml(fieldName, dataTree);

  let validateMessage = null;
  if (isNil(fields)) {
    validateMessage = String.format(ReportPartFormConstants.MESSAGES.M02, fieldName);
  } else if (fields.length > 1) {
    validateMessage = String.format(ReportPartFormConstants.MESSAGES.M01, fieldName);
  }

  return validateMessage;
};

/**
 * Validate field name alias.
 * @param  {Object} field
 * @param  {Object} context
 * @return {String} error message
 */
const validateFieldNameAlias = (field: any, context: any): any => {
  const fieldNameAliases = context.fieldNameAliases;
  if (isNil(fieldNameAliases)) return null;

  const fieldNameAlias = trim($izenda(field).text());
  const matchingItems = filter(item => item === fieldNameAlias, fieldNameAliases);

  return matchingItems.length > 1 ? String.format(ReportPartFormConstants.MESSAGES.M16, fieldNameAlias) : null;
};

/**
 * Build a field-prop tag with supplied key and value
 * @param  {String} key
 * @param  {Object} value
 * @return {Object} field
 */
const generateHTMLFieldPropTag = (key: string, value: any): any => {
  const compiledTemplateWithValueFn = template('<field-prop key="<%- key %>" value="<%- value %>"></field-prop>');
  if (!FieldPropertiesPaths[key]) {
    return '';
  }
  const defaultConfigField = new ReportPartElement();
  const defaultValue = get(FieldPropertiesPaths[key].path, defaultConfigField);
  // We only render props has value and its value is not a default value of its data type.
  if (
    (!isNil(defaultValue) && value !== defaultValue) ||
    (isNil(defaultValue) && !FieldPropertiesPaths[key].dataType.isDefaultValue(value))
  ) {
    value = value.toJS ? value.toJS() : value;

    if (isArray(value)) {
      value = JSON.stringify(value);
    }

    return compiledTemplateWithValueFn({
      key: key,
      value: value
    });
  }
  return '';
};

/**
 * Build html content for field properties
 * @param  {Object} configField
 * @param  {Object} isInitial
 * @return {String} field content
 */
const buildHTMLCodeFieldContent = (configField: any, isInitial: any): string => {
  let result = '';
  let keys = Object.keys(FieldPropertiesPaths);
  const excludedKeys = Object.keys(ExcludeOnHtmlViewPaths);
  keys = difference(keys, excludedKeys);
  const defaultConfigField = new ReportPartElement();

  if (isInitial) {
    keys = Object.keys(InitialFieldPropertiesPaths);
  }

  keys.forEach(key => {
    const value = get(FieldPropertiesPaths[key].path, configField);
    const defaultValue = get(FieldPropertiesPaths[key].path, defaultConfigField);
    !isNil(value) && value !== defaultValue && (result += generateHTMLFieldPropTag(key, value));
  });

  return result;
};

/**
 * Validate embedded report
 * @param {String} embeddedReportContent
 * @param {Object} embedde report data
 * @returns {Object} error messages or object
 */
const validateEmbeddedReport = (emId: string, embeddedReportContent: any): any => {
  const contentList = split('/', embeddedReportContent);
  return embedFetcher.validateEmbeddedReport(null, emId, contentList[0], contentList[1]).then(result => {
    if (!isNil(result.success) && !result.success) {
      return result.messages[0].messages[0];
    }
    return result;
  });
};

/**
 * Get html between two elements
 * @param {Object} first element
 * @param {Object} second element
 * @param {String} html content
 * @returns {String} text
 */
const getHtmlBetweenTwoElement = (e1: any, e2: any, htmlContent: string): string => {
  const editorHtml = $izenda('<div>' + htmlContent + '</div>').html();
  const e1Html = $izenda(e1).get(0).outerHTML;
  const e2Html = $izenda(e2).get(0).outerHTML;
  return editorHtml.substring(editorHtml.indexOf(e1Html) + e1Html.length, editorHtml.indexOf(e2Html));
};

/**
 * Clean html string from TinyMCE.
 * @param  {String} htmlContent
 * @return {String} html content
 */
const cleanHtmlFromTinyMCE = (htmlContent: string): string => {
  const $htmlContentWrapper = $izenda('<div/>').html(htmlContent);
  $izenda('field', $htmlContentWrapper)
    .removeAttr('contenteditable')
    .removeAttr('data-mce-selected');
  return $izenda($htmlContentWrapper)[0].innerHTML;
};

/**
 * Check a filed whether inside repeater element
 * @param {String} htmlContent
 * @param {String} fieldContent
 * @return {Boolean} result
 */
const checkFieldInsideRepeater = (htmlContent: string, fieldContent: string): boolean => {
  let repeaterStartTag, repeaterEndTag;

  if (htmlContent.indexOf(TinyMCEConstants.REPEATER_START) >= 0) {
    repeaterStartTag = TinyMCEConstants.REPEATER_START;
    repeaterEndTag = TinyMCEConstants.REPEATER_END;
  } else if (htmlContent.indexOf(TinyMCEConstants.REPEATER_START_FORTABLE) >= 0) {
    repeaterStartTag = TinyMCEConstants.REPEATER_START_FORTABLE;
    repeaterEndTag = TinyMCEConstants.REPEATER_END_FORTABLE;
  } else {
    return false;
  }

  const start = htmlContent.indexOf(repeaterStartTag);
  const end = htmlContent.lastIndexOf(repeaterEndTag);
  const content = htmlContent.substring(start, end);

  return content.indexOf(fieldContent) >= 0;
};

export default {
  syncFromHTMLToElements,
  generateHTMLFieldPropTag,
  FieldPropertiesPaths,
  validateField,
  buildHTMLCodeFieldContent,
  validateEmbeddedReport,
  getHtmlBetweenTwoElement,
  filterByNodeName,
  cleanHtmlFromTinyMCE,
  mapFromHTMLToObject
};



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartFormUtils.js